import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

/**
 * Layout mode types
 * - constrained: Uses Material UI Container with maxWidth constraint (default)
 * - full-width: Uses full viewport width without Container constraint
 */
export type LayoutMode = 'constrained' | 'full-width';

/**
 * Layout context type definition
 */
export type LayoutContextType = {
  layoutMode: LayoutMode;
  setLayoutMode: (mode: LayoutMode) => void;
  resetLayoutMode: () => void;
};

/**
 * Layout context provider props
 */
export type LayoutProviderProps = {
  children: ReactNode;
  defaultMode?: LayoutMode;
};

// Create the layout context
const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

/**
 * Layout Context Provider
 * Manages layout mode state across the application
 * Provides methods to switch between constrained and full-width layouts
 */
export const LayoutProvider = ({ children, defaultMode = 'constrained' }: LayoutProviderProps) => {
  const [layoutMode, setLayoutModeState] = useState<LayoutMode>(defaultMode);

  const setLayoutMode = (mode: LayoutMode) => {
    setLayoutModeState(mode);
  };

  const resetLayoutMode = () => {
    setLayoutModeState(defaultMode);
  };

  const contextValue: LayoutContextType = {
    layoutMode,
    setLayoutMode,
    resetLayoutMode
  };

  return <LayoutContext.Provider value={contextValue}>{children}</LayoutContext.Provider>;
};

/**
 * Custom hook to use layout context
 * Provides access to layout mode state and control functions
 *
 * @throws Error if used outside of LayoutProvider
 * @returns LayoutContextType with current layout mode and control functions
 */
export const useLayoutContext = (): LayoutContextType => {
  const context = useContext(LayoutContext);

  if (context === undefined) {
    throw new Error('useLayoutContext must be used within a LayoutProvider');
  }

  return context;
};

/**
 * Custom hook for components that need full-width layout
 * Automatically sets layout to full-width on mount and resets on unmount
 *
 * @param enabled - Whether to enable full-width mode (default: true)
 */
export const useFullWidthLayout = (enabled: boolean = true) => {
  const { setLayoutMode, resetLayoutMode } = useLayoutContext();

  useEffect(() => {
    if (enabled) {
      setLayoutMode('full-width');

      // Cleanup: reset to constrained mode when component unmounts
      return () => {
        resetLayoutMode();
      };
    }
  }, [enabled, setLayoutMode, resetLayoutMode]);
};

/**
 * Custom hook for components that need constrained layout
 * Explicitly sets layout to constrained mode
 *
 * @param enabled - Whether to enable constrained mode (default: true)
 */
export const useConstrainedLayout = (enabled: boolean = true) => {
  const { setLayoutMode, resetLayoutMode } = useLayoutContext();

  useEffect(() => {
    if (enabled) {
      setLayoutMode('constrained');

      // Cleanup: reset to default mode when component unmounts
      return () => {
        resetLayoutMode();
      };
    }
  }, [enabled, setLayoutMode, resetLayoutMode]);
};

export default LayoutContext;
